/**
 * <estimated-delivery>
 *
 * Renders a delivery-date estimate from `data-*` attributes set by
 * snippets/estimated-delivery.liquid. The calculation runs client-side so
 * the visitor's actual timezone, the configured cutoff time, and "today"
 * are all evaluated correctly even when the section is served from the
 * Shopify CDN cache.
 *
 * Required attributes (all set by the liquid snippet):
 *   data-min-days           integer  Minimum lead time in business days
 *   data-max-days           integer  Maximum lead time in business days
 *   data-cutoff-time        "HH:MM"  Daily cutoff after which today no longer counts
 *   data-cutoff-tz          IANA     Timezone for the cutoff comparison
 *   data-skip-weekends      "true"|"false"
 *   data-holidays           Comma- or whitespace-separated list of YYYY-MM-DD dates
 *   data-date-style         "short" | "compact" | "long" — how each date placeholder is rendered
 *   data-locale             BCP-47 locale, e.g. "de" or "en-US"
 *   data-text-template      Label template (placeholders: {date}, {min_date}, {max_date}, {min_days}, {max_days})
 *   data-countdown-template Optional. Label template used while before cutoff
 *                           (placeholders: {countdown}, {hours}, {minutes}, {seconds}, plus all date placeholders)
 *   data-unit-hours         Short label for hours, e.g. "Std." (locale-driven)
 *   data-unit-minutes       Short label for minutes, e.g. "Min."
 *   data-unit-seconds       Short label for seconds, e.g. "Sek."
 *
 * Updates automatically on the global `variant:change` event and ticks every
 * second while the countdown template is active.
 */

class EstimatedDelivery extends HTMLElement {
  connectedCallback() {
    this._output = this.querySelector('.estimated-delivery__output');
    if (!this._output) return;

    this._abort = new AbortController();
    this._tickHandle = null;
    this._render();

    document.addEventListener('variant:change', this._onVariantChange.bind(this), {
      signal: this._abort.signal,
    });
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._stopTick();
  }

  _onVariantChange(event) {
    const variant = event?.detail?.variant;
    if (!variant) return;
    // The liquid snippet re-renders the surrounding section on variant change,
    // which replaces this element entirely with fresh data attrs. We only
    // re-render here as a safety net for themes that don't trigger a section
    // refresh — values from data-* are still the latest ones.
    this._render();
  }

  _stopTick() {
    if (this._tickHandle) {
      clearInterval(this._tickHandle);
      this._tickHandle = null;
    }
  }

  _render() {
    try {
      const minDays = parseInt(this.dataset.minDays, 10);
      const maxDays = parseInt(this.dataset.maxDays, 10);
      if (Number.isNaN(minDays) || Number.isNaN(maxDays)) return;

      const min = Math.min(minDays, maxDays);
      const max = Math.max(minDays, maxDays);
      const cutoffTime = (this.dataset.cutoffTime || '14:00').trim();
      const cutoffTz = (this.dataset.cutoffTz || 'Europe/Berlin').trim();
      const skipWeekends = this.dataset.skipWeekends !== 'false';
      const holidays = this._parseHolidays(this.dataset.holidays || '');
      const dateStyle = this.dataset.dateStyle || 'short';
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const textTemplate = this.dataset.textTemplate || 'Delivery {min_date} – {max_date}';
      const countdownTemplate = this.dataset.countdownTemplate || '';

      const { startDay, secondsUntilCutoff } = this._computeStartInfo(cutoffTime, cutoffTz);
      const minDate = this._addBusinessDays(startDay, min, skipWeekends, holidays);
      const maxDate = this._addBusinessDays(startDay, max, skipWeekends, holidays);

      const useCountdown = countdownTemplate && secondsUntilCutoff > 0;
      const template = useCountdown ? countdownTemplate : textTemplate;

      const replacements = this._buildReplacements({
        minDate,
        maxDate,
        min,
        max,
        dateStyle,
        locale,
        secondsUntilCutoff: useCountdown ? secondsUntilCutoff : 0,
      });

      this._renderTemplate(template, replacements);

      // Schedule second-by-second tick while the countdown is active. When the
      // remaining seconds hit zero we re-render once with the non-countdown
      // template (start day shifts to tomorrow) and stop the timer.
      this._stopTick();
      if (useCountdown) {
        this._tickHandle = setInterval(() => this._render(), 1000);
      }
    } catch (err) {
      // Fall back to whatever the noscript content provided. Never throw — the
      // delivery estimate is a nice-to-have, not critical.
      // console.warn('[estimated-delivery]', err);
    }
  }

  _parseHolidays(raw) {
    if (!raw) return new Set();
    return new Set(
      raw
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s))
    );
  }

  /**
   * Returns:
   *  - startDay: local-midnight Date of the day from which business-day
   *    counting begins (today if before cutoff, tomorrow otherwise).
   *  - secondsUntilCutoff: how many seconds remain until the daily cutoff
   *    at the warehouse, or 0 if we're already past it. Drives the countdown.
   */
  _computeStartInfo(cutoffTime, cutoffTz) {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: cutoffTz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(now);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const tzDate = `${get('year')}-${get('month')}-${get('day')}`;
    const tzHour = parseInt(get('hour'), 10);
    const tzMinute = parseInt(get('minute'), 10);
    const tzSecond = parseInt(get('second'), 10) || 0;

    const [cutH, cutM] = cutoffTime.split(':').map((n) => parseInt(n, 10));
    const nowSec = tzHour * 3600 + tzMinute * 60 + tzSecond;
    const cutSec = cutH * 3600 + cutM * 60;
    const pastCutoff = nowSec >= cutSec;
    const secondsUntilCutoff = pastCutoff ? 0 : cutSec - nowSec;

    const [y, m, d] = tzDate.split('-').map((n) => parseInt(n, 10));
    const startDay = new Date(y, m - 1, d);
    if (pastCutoff) startDay.setDate(startDay.getDate() + 1);

    return { startDay, secondsUntilCutoff };
  }

  _isSkippable(date, skipWeekends, holidays) {
    if (skipWeekends) {
      const dow = date.getDay();
      if (dow === 0 || dow === 6) return true;
    }
    if (holidays.size > 0) {
      const iso = this._toIsoDate(date);
      if (holidays.has(iso)) return true;
    }
    return false;
  }

  _addBusinessDays(start, count, skipWeekends, holidays) {
    const date = new Date(start);
    if (count <= 0) {
      // Even with 0 days lead time, if start lands on a skipped day, advance.
      while (this._isSkippable(date, skipWeekends, holidays)) {
        date.setDate(date.getDate() + 1);
      }
      return date;
    }
    let remaining = count;
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      if (!this._isSkippable(date, skipWeekends, holidays)) {
        remaining -= 1;
      }
    }
    return date;
  }

  _toIsoDate(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  _intlOptionsForStyle(style) {
    switch (style) {
      case 'compact':
        return { day: 'numeric', month: 'short' };
      case 'long':
        return { weekday: 'long', day: 'numeric', month: 'long' };
      case 'short':
      default:
        return { weekday: 'short', day: 'numeric', month: 'short' };
    }
  }

  _formatDate(date, locale, style) {
    const opts = this._intlOptionsForStyle(style);
    try {
      return new Intl.DateTimeFormat(locale, opts).format(date);
    } catch (_) {
      return new Intl.DateTimeFormat('en', opts).format(date);
    }
  }

  _formatCountdown(totalSeconds) {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const uH = this.dataset.unitHours || 'hr';
    const uM = this.dataset.unitMinutes || 'min';
    const uS = this.dataset.unitSeconds || 'sec';

    // Granularity matches Amazon's pattern: hours+minutes while >1h remain,
    // minutes+seconds inside the last hour, raw seconds in the last minute.
    if (hours > 0) return `${hours} ${uH} ${minutes} ${uM}`;
    if (minutes > 0) return `${minutes} ${uM} ${seconds} ${uS}`;
    return `${seconds} ${uS}`;
  }

  _buildReplacements({ minDate, maxDate, min, max, dateStyle, locale, secondsUntilCutoff }) {
    const minStr = this._formatDate(minDate, locale, dateStyle);
    const maxStr = this._formatDate(maxDate, locale, dateStyle);

    // `{date}` is a smart placeholder — single when min/max land on the same
    // day, range otherwise. Use {min_date} / {max_date} explicitly to force one.
    const dateSmart = minDate.getTime() === maxDate.getTime() ? maxStr : `${minStr} – ${maxStr}`;

    const hours = secondsUntilCutoff > 0 ? Math.floor(secondsUntilCutoff / 3600) : 0;
    const minutes = secondsUntilCutoff > 0 ? Math.floor((secondsUntilCutoff % 3600) / 60) : 0;
    const seconds = secondsUntilCutoff > 0 ? secondsUntilCutoff % 60 : 0;

    return {
      '{date}': dateSmart,
      '{min_date}': minStr,
      '{max_date}': maxStr,
      '{min_days}': String(min),
      '{max_days}': String(max),
      '{countdown}': secondsUntilCutoff > 0 ? this._formatCountdown(secondsUntilCutoff) : '',
      '{hours}': String(hours),
      '{minutes}': String(minutes),
      '{seconds}': String(seconds),
    };
  }

  _renderTemplate(template, replacements) {
    let out = template;
    for (const [key, value] of Object.entries(replacements)) {
      out = out.split(key).join(value);
    }
    // Templates are merchant-controlled (theme settings, not user input), so
    // innerHTML is safe and lets the merchant emphasise key words with <strong>.
    this._output.innerHTML = out;
  }
}

if (!window.customElements.get('estimated-delivery')) {
  window.customElements.define('estimated-delivery', EstimatedDelivery);
}
